import { Button } from '@sunwebgroup/uds-ui/button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from '@/intl'
import { Loader } from '@/scopes/checkout/components/Layout'
import { createGroupsEvent } from '@/scopes/checkout/domain/analytics'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { FlexContent } from '@/scopes/checkout/groups/components/services/RoomSelection/FlexContent'

import styles from '@/scopes/checkout/groups/components/services/RoomSelection/RoomSelectionService.module.css'
import {
  getMethodAllocationFromSelectedRoom,
  getNewRoomBeingCreated,
  getPinCodeFromSelectedRoom,
  getSelectedRoom,
  isAnyRoomAlreadyNamed,
  isAnyRoomTypeAvailable,
  isNewRoomBeingCreated,
} from '@/scopes/checkout/groups/components/services/RoomSelection/selectors'
import {
  AssignationMethodFormField,
} from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/FormFields/AssignationMethodFormField'
import {
  PinCodeFormField,
} from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/FormFields/PinCodeFormField'
import {
  RoomNameFormField,
} from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/FormFields/RoomNameFormField'
import {
  RoomTypeFormField,
} from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/FormFields/RoomTypeFormField'
import {
  SelectNamedRoomFormField,
} from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/FormFields/SelectNamedRoomFormField'
import {
  RoomSelectionEarlyBirdBanner,
} from '@/scopes/checkout/groups/components/services/RoomSelection/Sheet/RoomSelectionEarlyBirdBanner'
import {
  useRoomSelectionServiceStore,
} from '@/scopes/checkout/groups/components/services/RoomSelection/store'
import { useAvailableRoomsQuery, useRoom } from '@/scopes/checkout/groups/domain/hooks'
import { RoomSelectionTypeRadioGroupOptions } from '@/scopes/checkout/groups/domain/types/availableRooms'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

interface FormValues {
  assignationMethod: RoomSelectionTypeRadioGroupOptions
  newRoomName: string | null
  roomType: string | null
  pinCode?: string
  isNewRoom: boolean | null
  selectedRoomId: string | null
}

export function RoomSelectionForm() {
  const { isLoading, data } = useAvailableRoomsQuery()
  const { data: bookingDraftData } = useBookingDraftQuery()
  const { setRoomValueAsync, setRoomValue } = useRoom()
  const { rooms, roomTypes } = data || {}
  const { endSelectingRoomService } = useRoomSelectionServiceStore()
  const [isAssignationMethodSpecific, setIsAssignationMethodSpecific] = React.useState(true)
  const intl = useIntl()
  const selectedMethodAllocation = getMethodAllocationFromSelectedRoom(bookingDraftData)
  const selectedPinCode = getPinCodeFromSelectedRoom(bookingDraftData)
  const selectedRoom = getSelectedRoom(rooms, bookingDraftData)
  const newRoomBeingCreated = isNewRoomBeingCreated(bookingDraftData)
  const newRoom = getNewRoomBeingCreated(roomTypes, bookingDraftData)
  const { sendEvent } = useAnalytics()
  const [isBeingSubmitted, setIsBeingSubmitted] = React.useState(false)

  const form = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      assignationMethod: selectedMethodAllocation || 'specific',
      isNewRoom: newRoomBeingCreated || false,
      newRoomName: newRoom?.name || '',
      roomType: newRoom?.type || null,
      pinCode: selectedPinCode || undefined,
      selectedRoomId: selectedRoom?.roomId || null,
    },
  })
  const { handleSubmit, watch } = form
  const onSubmit = React.useCallback(async (result: FormValues) => {
    setIsBeingSubmitted(true)
    const selectedRoomName = result.isNewRoom ? result.newRoomName : (rooms && result.selectedRoomId) ? rooms[result.selectedRoomId]?.name : null
    if (!result.isNewRoom && result.selectedRoomId) {
      const response = await setRoomValueAsync({
        assignationMethod: result.assignationMethod,
        name: selectedRoomName || null,
        type: result.isNewRoom ? result.roomType : null,
        pinCode: result.pinCode,
        isNewRoom: result.isNewRoom || false,
        roomId: result.selectedRoomId || null,
      })

      if (!response.isValid) {
        form.setError('pinCode', { type: 'backendInvalid' })
        setIsBeingSubmitted(false)
        return
      }
    }
    else {
      setRoomValue({
        assignationMethod: result.assignationMethod,
        name: selectedRoomName || null,
        type: result.isNewRoom ? result.roomType : null,
        pinCode: result.pinCode,
        isNewRoom: result.isNewRoom || false,
        roomId: result.selectedRoomId || null,
      })
      setIsBeingSubmitted(false)
    }

    sendEvent(
      createGroupsEvent({
        description: `RoomSelection`,
        element: 'button',
        type: 'submit',
        result: `room selection submitted`,
      }),
    )
    endSelectingRoomService()
  }, [sendEvent, setRoomValue, setRoomValueAsync, endSelectingRoomService, rooms, form])

  const watchedAssignationMethodValue = watch(`assignationMethod`) || ''
  const isCreatingNamedRoom = watch('isNewRoom')

  const isAnyRoomNamed = isAnyRoomAlreadyNamed(rooms)
  React.useEffect(() => {
    const isSpecific = watchedAssignationMethodValue === 'specific'
    setIsAssignationMethodSpecific(isSpecific)
    if (!isAnyRoomNamed && isSpecific && isAnyRoomTypeAvailable(roomTypes)) {
      form.setValue('isNewRoom', true)
    }
  }, [watchedAssignationMethodValue, isAnyRoomNamed, form, roomTypes])

  return (
    <>
      <Sheet.Content className={styles.fullHeight}>
        {isLoading && <Loader />}
        {!isLoading && (
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <FlexContent gap="primitives-space-12">
                <AssignationMethodFormField />
                { isAssignationMethodSpecific && (
                  <>
                    {isAnyRoomNamed && (
                      <SelectNamedRoomFormField />
                    )}
                    { (!isAnyRoomNamed || isCreatingNamedRoom) && (
                      <CreateNamedRoomFields />
                    )}
                  </>
                )}
              </FlexContent>
            </form>
          </FormProvider>
        )}
      </Sheet.Content>
      <Sheet.Footer>
        {!isLoading && (
          <Button variant="primary" onClick={handleSubmit(onSubmit)} fullWidth disabled={isBeingSubmitted} isLoading={isBeingSubmitted}>
            {intl.formatMessage({ id: 'groups_checkout_steps_room_selection_sheet_accept_text_button' })}
          </Button>
        )}
      </Sheet.Footer>
    </>
  )
}

function CreateNamedRoomFields() {
  const { data } = useAvailableRoomsQuery()
  const { data: bookingDraftData } = useBookingDraftQuery()
  const { rooms, roomTypes } = data || {}
  const isAnyRoomNamed = isAnyRoomAlreadyNamed(rooms)
  const isAnyRoomTypeAvailableToCreateANamedRoom = isAnyRoomTypeAvailable(roomTypes)
  const intl = useIntl()

  return (
    <>
      {!isAnyRoomNamed && isAnyRoomTypeAvailableToCreateANamedRoom && <RoomSelectionEarlyBirdBanner />}
      {isAnyRoomNamed && (
        <Typography variant="display-h-regular">
          {intl.formatMessage({ id: 'groups_checkout_steps_room_selection_new_room_title' })}
        </Typography>
      )}
      <RoomNameFormField />
      {bookingDraftData?.groupInformation?.pinCodeIsRequiredForRoomSelection && (<PinCodeFormField helpText={intl.formatMessage({ id: 'groups_checkout_steps_room_selection_pin_code_help_text' })} />)}
      <RoomTypeFormField />
    </>
  )
}
